import type { RefObject } from "react";
import SearchOutlineIcon from "./assets/SearchOutlineIcon";
import { Button } from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { cn } from "@/libs/utils/tailwind/cn";
import SearchDropdown from "./components/SearchDropdown";
import ServicesModal from "./components/ServicesModal";
import { useSearchServicesInput } from "./hooks/useSearchServicesInput";

export default function SearchServicesInput({
  className,
  inputRef,
}: SearchServicesInputProps) {
  const {
    searchQuery,
    updateSearchQuery,
    openResultsDropdown,
    handleInputBlur,
    handleInputKeyDown,
    isDropdownOpen,
    dropdownRef,
    services,
    isFetching,
    selectedIndex,
    itemRefs,
    isServicesModalOpen,
    modalServices,
    closeServicesModal,
    selectService,
    submitSearch,
    setSelectedIndex,
  } = useSearchServicesInput();

  return (
    <div className={cn("relative", className)}>
      <Input
        inputRef={inputRef}
        value={searchQuery}
        onValueChange={updateSearchQuery}
        onFocus={openResultsDropdown}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        placeholder="What type of pro is needed?"
        classNames={{
          base: "bg-white flex-1 rounded-2xl p-2 pl-4 [-webkit-tap-highlight-color:transparent]",
          inputWrapper: "h-10 sm:h-14 gap-4 bg-transparent shadow-none rounded-none data-[hover=true]:bg-transparent group-data-[focus=true]:bg-transparent group-data-[focus-visible=true]:ring-0 group-data-[focus-visible=true]:outline-none pl-2 pr-0",
          innerWrapper: "gap-2 min-h-10 sm:min-h-14",
          input: "placeholder:!text-default-800 !text-[14px] sm:!text-lg font-normal focus:outline-none focus-visible:outline-none [-webkit-tap-highlight-color:transparent]",
        }}
        endContent={
          <Button
            isIconOnly
            color="primary"
            radius="full"
            size="lg"
            className="shrink-0 min-w-10 w-10 h-10 sm:w-14 rounded-2xl sm:h-14 self-stretch flex justify-center items-center bg-secondary"
            aria-label="Search"
            onPress={submitSearch}
          >
            <SearchOutlineIcon className="size-5 sm:size-6 text-white" />
          </Button>
        }
      />

      {/* Dropdown */}
      {isDropdownOpen && (
        <div ref={dropdownRef}>
          <SearchDropdown
            services={services}
            isFetching={isFetching}
            selectedIndex={selectedIndex}
            inputValue={searchQuery}
            itemRefs={itemRefs}
            onServiceSelect={selectService}
            onMouseEnter={setSelectedIndex}
          />
        </div>
      )}

      <ServicesModal
        isOpen={isServicesModalOpen}
        services={modalServices}
        onClose={closeServicesModal}
      />
    </div>
  );
}

interface SearchServicesInputProps {
  className?: string;
  inputRef?: RefObject<HTMLInputElement>;
}
